import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { COMPANY, SERVICES, NAV_LINKS } from "@/data/site";

const Footer = () => (
  <footer data-testid="site-footer" className="relative bg-navy-deep border-t border-white/5 grain overflow-hidden">
    <div className="max-w-8xl mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
        <div className="lg:col-span-4">
          <Link to="/" className="inline-block bg-white rounded-2xl px-5 py-3" data-testid="footer-logo-link">
            <img src="/assets/logo.png" alt="Click World Online logo" className="h-12 w-auto" />
          </Link>
          <p className="mt-6 text-slatec text-sm leading-relaxed max-w-xs">
            A digital marketing agency in Mansa, Punjab helping businesses strengthen their digital presence through
            strategy, creativity, technology and performance-focused marketing.
          </p>
          <p data-testid="footer-established" className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-mist/50 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-electric" aria-hidden="true" />
            Established 30 April 2017
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              href={COMPANY.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Click World Online on Instagram"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-mist/70 hover:text-electric hover:border-electric/40 transition-colors duration-300"
            >
              <Instagram size={17} />
            </a>
            <a
              href={COMPANY.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              aria-label="Click World Online on Facebook"
              className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-mist/70 hover:text-electric hover:border-electric/40 transition-colors duration-300"
            >
              <Facebook size={17} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h3 className="text-xs uppercase tracking-[0.2em] text-mist/40 font-semibold mb-5">Explore</h3>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm text-slatec hover:text-electric transition-colors duration-200">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/case-studies" data-testid="footer-nav-case-studies" className="text-sm text-slatec hover:text-electric transition-colors duration-200">
                Case Studies
              </Link>
            </li>
            <li>
              <Link to="/insights" data-testid="footer-nav-insights" className="text-sm text-slatec hover:text-electric transition-colors duration-200">
                Insights
              </Link>
            </li>
            <li>
              <Link to="/faq" data-testid="footer-nav-faq" className="text-sm text-slatec hover:text-electric transition-colors duration-200">
                FAQs
              </Link>
            </li>
            <li>
              <Link to="/service-areas" data-testid="footer-nav-service-areas" className="text-sm text-slatec hover:text-electric transition-colors duration-200">
                Service Areas
              </Link>
            </li>
            <li>
              <Link to="/careers" data-testid="footer-careers-link" className="text-sm text-slatec hover:text-electric transition-colors duration-200">
                Careers — Join Our Team
              </Link>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="text-xs uppercase tracking-[0.2em] text-mist/40 font-semibold mb-5">Services</h3>
          <ul className="space-y-3 columns-1">
            {SERVICES.slice(0, 8).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.slug}`} className="text-sm text-slatec hover:text-electric transition-colors duration-200">
                  {s.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/services" data-testid="footer-all-services" className="text-sm text-electric">
                View all services →
              </Link>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="text-xs uppercase tracking-[0.2em] text-mist/40 font-semibold mb-5">Contact</h3>
          <ul className="space-y-4 text-sm text-slatec">
            <li className="flex gap-3">
              <MapPin size={16} className="text-electric shrink-0 mt-0.5" />
              <span data-testid="footer-address">{COMPANY.address1.replace("H.No. ", "#")}, {COMPANY.address2}</span>
            </li>
            <li className="flex gap-3">
              <Phone size={16} className="text-electric shrink-0 mt-0.5" />
              <span className="flex flex-col gap-1">
                <a href={`tel:${COMPANY.phone1.replace(/\s/g, "")}`} data-testid="footer-phone-1" className="hover:text-electric transition-colors">{COMPANY.phone1}</a>
                <a href={`tel:${COMPANY.phone2.replace(/\s/g, "")}`} data-testid="footer-phone-2" className="hover:text-electric transition-colors">{COMPANY.phone2}</a>
              </span>
            </li>
            <li className="flex gap-3">
              <Mail size={16} className="text-electric shrink-0 mt-0.5" />
              <span className="flex flex-col gap-1">
                <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="hover:text-electric transition-colors">{COMPANY.email}</a>
                <a href={`mailto:${COMPANY.email2}`} data-testid="footer-email-2" className="hover:text-electric transition-colors">{COMPANY.email2}</a>
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-white/5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-mist/40">
        <span className="uppercase tracking-[0.2em]">Areas we serve</span>
        <Link to="/locations/mansa" data-testid="footer-area-mansa" className="hover:text-electric transition-colors">Mansa</Link>
        <span aria-hidden="true">·</span>
        <Link to="/locations/bathinda" data-testid="footer-area-bathinda" className="hover:text-electric transition-colors">Bathinda</Link>
        <span aria-hidden="true">·</span>
        <Link to="/locations/chandigarh" data-testid="footer-area-chandigarh" className="hover:text-electric transition-colors">Chandigarh</Link>
        <span aria-hidden="true">·</span>
        <Link to="/locations/mohali" data-testid="footer-area-mohali" className="hover:text-electric transition-colors">Mohali</Link>
        <span aria-hidden="true">·</span>
        <Link to="/locations/punjab" data-testid="footer-area-punjab" className="hover:text-electric transition-colors">Punjab</Link>
        <span aria-hidden="true">·</span>
        <Link to="/service-areas" data-testid="footer-area-international" className="hover:text-electric transition-colors">Across India &amp; remote worldwide — Canada, USA, UK, Australia</Link>
      </div>

      <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs text-mist/40">
          © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
        </p>
        <div className="flex flex-wrap gap-6 text-xs text-mist/40">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-electric transition-colors">Privacy Policy</Link>
          <Link to="/terms-and-conditions" data-testid="footer-terms-link" className="hover:text-electric transition-colors">Terms & Conditions</Link>
          <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="hover:text-electric transition-colors">Disclaimer</Link>
        </div>
      </div>
    </div>

    <div className="pointer-events-none select-none overflow-hidden pb-2" aria-hidden="true">
      <p className="font-display font-black text-outline text-[13vw] leading-none whitespace-nowrap text-center tracking-tight opacity-60">
        CLICK WORLD ONLINE
      </p>
    </div>
  </footer>
);

export default Footer;
